import { useEffect } from 'react';

/**
 * Keyboard shortcuts for the POS screen (Phase-1 expanded set).
 *
 * F1   = Focus search
 * F2   = Open customer selector
 * F3   = Hold/Park current sale
 * F4   = Recall parked sale
 * F5   = Pay (Cash default)
 * F6   = Toggle Split payment
 * F7   = Transaction history
 * F8   = Void (clear cart)
 * F9   = Return mode
 * F10  = Exchange flow (return + new sale)
 * F11  = Header discount
 * Esc  = Close modal / exit
 * +/-  = Increment / decrement selected item qty
 * Del  = Remove selected cart line
 * Ctrl+P = Reprint last receipt
 * Ctrl+R = Repeat last sale
 * Ctrl+D = Duplicate selected line
 * ArrowUp/Down = Cart navigation
 */
const usePOSKeyboard = (handlers = {}, enabled = true) => {
  useEffect(() => {
    if (!enabled) return;

    const handleKeyDown = (e) => {
      const tag = e.target.tagName;
      const isFunctionKey = e.key.startsWith('F') && e.key.length <= 3;
      const isInput = tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
      const isCtrl = e.ctrlKey || e.metaKey;

      // Inside an input, only allow function keys, Esc, and ctrl-combos through.
      if (isInput && !isFunctionKey && e.key !== 'Escape' && !isCtrl) return;

      // Ctrl-combos
      if (isCtrl) {
        const k = e.key.toLowerCase();
        if (k === 'p') { e.preventDefault(); handlers.onReprintLast?.(); return; }
        if (k === 'r') { e.preventDefault(); handlers.onRepeatLast?.(); return; }
        if (k === 'd') { e.preventDefault(); handlers.onDuplicateLine?.(); return; }
      }

      switch (e.key) {
        case 'F1': e.preventDefault(); handlers.onSearch?.(); break;
        case 'F2': e.preventDefault(); handlers.onCustomer?.(); break;
        case 'F3': e.preventDefault(); handlers.onHold?.(); break;
        case 'F4': e.preventDefault(); handlers.onRecall?.(); break;
        case 'F5': e.preventDefault(); handlers.onPay?.(); break;
        case 'F6': e.preventDefault(); handlers.onSplit?.(); break;
        case 'F7': e.preventDefault(); handlers.onHistory?.(); break;
        case 'F8': e.preventDefault(); handlers.onVoid?.(); break;
        case 'F9': e.preventDefault(); handlers.onReturn?.(); break;
        case 'F10': e.preventDefault(); handlers.onExchange?.(); break;
        case 'F11': e.preventDefault(); handlers.onDiscount?.(); break;
        case 'Escape': e.preventDefault(); handlers.onEscape?.(); break;
        case '+':
          if (!isInput) { e.preventDefault(); handlers.onIncrementQty?.(); }
          break;
        case '-':
          if (!isInput) { e.preventDefault(); handlers.onDecrementQty?.(); }
          break;
        case 'Delete':
          if (!isInput) { e.preventDefault(); handlers.onRemoveLine?.(); }
          break;
        case 'ArrowUp':
          if (!isInput) { e.preventDefault(); handlers.onCartUp?.(); }
          break;
        case 'ArrowDown':
          if (!isInput) { e.preventDefault(); handlers.onCartDown?.(); }
          break;
        default: break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handlers, enabled]);
};

export default usePOSKeyboard;
